import type * as stylex from "@stylexjs/stylex";
import { Children, Fragment, isValidElement, type ReactNode } from "react";
import { ShineBorder } from "@/components/custom/shine-border";
import { BlurFade } from "@/components/ui/blur-fade";
import { cn } from "@/lib/utils";
import { CardDescription, CardHeader, CardTitle } from "../ui/card";
export function PageIntroSplitHeader(props: {
  kicker: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  metrics?: ReactNode;
  metricsLabel?: string | null;
  headerClassName?: string;
  titleClassName?: string;
  titleStyle?: stylex.StyleXStyles;
  descriptionClassName?: string;
  leftPanelClassName?: string;
  rightPanelClassName?: string;
  layoutClassName?: string;
  enableMotion?: boolean;
}) {
  const {
    kicker,
    title,
    description,
    actions,
    metrics,
    metricsLabel = "",
    headerClassName,
    titleClassName,
    titleStyle,
    descriptionClassName,
    leftPanelClassName,
    rightPanelClassName,
    layoutClassName,
    enableMotion = true
  } = props;
  const hasMetrics = metrics !== undefined && metrics !== null;
  const withMotion = (content: ReactNode, delay: number) => {
    if (!enableMotion) return content;
    return <BlurFade delay={delay} duration={0.35} offset={10} blur="8px">
        {content}
      </BlurFade>;
  };
  const metricItems = Children.toArray(metrics).flatMap(metric => {
    if (isValidElement<{
      children?: ReactNode;
    }>(metric) && metric.type === Fragment) {
      return Children.toArray(metric.props.children);
    }
    return [metric];
  });
  const renderedMetrics = metricItems.map((metric, index) => {
    return <div key={`intro-metric-${index}`} className="relative overflow-hidden rounded-[1.1rem] p-px [&>.metric-tile]:border-transparent">
        <ShineBorder borderWidth={1} duration={12} shineColor={["var(--accent)", "var(--primary)", "var(--secondary)"]} />
        <div className="relative">{metric}</div>
      </div>;
  });
  return <CardHeader className={cn("relative gap-4 md:gap-5", headerClassName)}>
      <div className={cn(hasMetrics ? "grid gap-4 xl:grid-cols-[minmax(0,1.18fr)_minmax(18rem,0.82fr)] xl:items-stretch" : "grid gap-4", layoutClassName)}>
        <div className={cn("grid content-start items-start gap-4 rounded-[1.25rem] border border-border/60 bg-card/55 p-5 md:p-6", leftPanelClassName)}>
          {withMotion(kicker, 0)}
          {withMotion(<CardTitle className={titleClassName} stylexStyle={titleStyle}>
              {title}
            </CardTitle>, 0.03)}
          {description ? withMotion(<CardDescription className={descriptionClassName}>
                  {description}
                </CardDescription>, 0.06) : null}
          {actions ? withMotion(actions, 0.09) : null}
        </div>

        {hasMetrics ? <div className={cn("grid content-start gap-3 rounded-[1.25rem] border border-border/60 bg-background/55 p-4 md:p-5", rightPanelClassName)}>
            {metricsLabel ? withMotion(<div className="text-kicker text-[0.7rem] tracking-[0.2em] text-muted-foreground">
                    {metricsLabel}
                  </div>, 0.05) : null}
            {withMotion(<div className="grid gap-4 md:grid-cols-3 xl:grid-cols-1">
                {renderedMetrics}
              </div>, 0.08)}
          </div> : null}
      </div>
    </CardHeader>;
}